import * as React from "react"
import { H1Desktop } from "../atoms/typography/H1"
import { Col, Container, Row } from "react-grid-system"
import H3 from "../atoms/typography/H3"
import BodyMedium from "../atoms/typography/BodyMedium"
import { MailIcon } from "../atoms/icons/MailIcon"
import { ContactPhoneIcon } from "../atoms/icons/ContactPhoneIcon"
import Inputbox from "../molecules/input/Inputbox"
import { HorizontalSpacer, Spacer } from "../atoms/spacers"
import Button from "../atoms/buttons/Button"
import { SendIcon } from "../atoms/icons/SendIcon"
import TextArea from "../atoms/inputs/TextArea"
import { useContactUs } from "../../graphql/mutations/useContactUs"

interface ContactUsFormProps {
  title?: string
}

const ContactUsForm: React.FC<ContactUsFormProps> = ({ title = "Get in touch with us" }) => {
  const { contactUs, loading, error, data } = useContactUs()

  const onSubmit: React.FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault()
    const form = e.currentTarget
    const formData = new FormData(form)
    const variables = Object.fromEntries(formData)
    contactUs({ variables })
  }

  if (error) {
    return (
      <Container>
        <Row justify="center">
          <Col xs="content">
            <H1Desktop bold>{title}</H1Desktop>
            <HorizontalSpacer spacing={1.5} />
            <H3>Something went wrong..</H3>
          </Col>
        </Row>
      </Container>
    )
  }

  if (data?.contactUs.success) {
    return (
      <Container>
        <Row justify="center">
          <Col xs="content">
            <H1Desktop bold>Thank you for contacting us!</H1Desktop>
            <HorizontalSpacer spacing={1.5} />
            <H3>Our sales team will get back to you very soon.</H3>
          </Col>
        </Row>
      </Container>
    )
  }

  return (
    <Container>
      <Row justify="center">
        <Col xs="content">
          <H1Desktop bold>{title}</H1Desktop>
          <HorizontalSpacer spacing={1.5} />
          <H3>Contact us directly at:</H3>
          <HorizontalSpacer spacing={1} />
          <Row direction="column" style={{ rowGap: "16px" }}>
            {[
              { icon: <MailIcon />, text: "sales@satcube.com" },
              { icon: <ContactPhoneIcon />, text: "+46 31 701 05 00 (intl)" },
              /* { icon: <ContactPhoneIcon />, text: "+1 888 6175 180 (US)" }, */
            ].map((contactInfo) => (
              <Col xs={12}>
                <Row gutterWidth={4} align="center" wrap="nowrap">
                  <Col xs="content">{contactInfo.icon}</Col>
                  <Col xs="content">
                    <BodyMedium>{contactInfo.text}</BodyMedium>
                  </Col>
                </Row>
              </Col>
            ))}
          </Row>
          <HorizontalSpacer spacing={2.5} />
          <H3>Or use this form</H3>
          <HorizontalSpacer spacing={1} />
          <form onSubmit={onSubmit}>
            <Row>
              <Col xs={12} lg={6}>
                <Inputbox name="firstName" label="First name" placeholder="First name" />
                <HorizontalSpacer spacing={2} />
              </Col>
              <Col xs={12} lg={6}>
                <Inputbox name="lastName" label="Last name" placeholder="Last name" />
                <HorizontalSpacer spacing={2} />
              </Col>
            </Row>
            <Row>
              <Col xs={12} lg={6}>
                <Inputbox name="email" label="Email" placeholder="example@example.com" />
                <HorizontalSpacer spacing={2} />
              </Col>
              <Col xs={12} lg={6}>
                <Inputbox name="phone" label="Phone (optional)" placeholder="+ 00 xxxxxxxxx" />
                <HorizontalSpacer spacing={2} />
              </Col>
            </Row>
            <Row>
              <Col xs={12} lg={6}>
                <Inputbox name="company" label="Company (Optional)" placeholder="Company name" />
                <HorizontalSpacer spacing={2} />
              </Col>
              <Col xs={12} lg={6}>
                <Inputbox name="title" label="Title (optional)" placeholder="Work title" />
                <HorizontalSpacer spacing={2} />
              </Col>
            </Row>
            <HorizontalSpacer spacing={2} />
            <TextArea name="message" placeholder="Type your message here..." rows={5} />
            <HorizontalSpacer spacing={2} />
            <Button
              disabled={loading}
              type="submit"
              title="Submit"
              iconRight={<SendIcon />}
              size="normal"
              variant="dark"
            />
          </form>
          <HorizontalSpacer spacing={5} />
        </Col>
      </Row>
    </Container>
  )
}

export default ContactUsForm
